import { CHART_TYPE } from '@tencent/chart/lib/chart-ui/interface';
import { DataSet } from '@tencent/chart/lib/data-set';
import {
    applyBgToLineStroke, filterOriginStyle, generateCustomStyles,
    getShapeAttr,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/style';
import type {
    ChartUIDataItem,
    ShapeStyle,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-ui.interface';
import type {
    ITrendlineProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/geometry/trendline/trendline.interface';
import type { IChartRendererConfig } from '@tencent/chart/lib/chart-ui/interface';
import { SpecialGeometryType } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';
import { CATEGORY_MARK } from '@src/slide/domain/ui-model/element-modeles/property/chart/util/format';

export class Trendline {
    private data: ChartUIDataItem[];

    public constructor(private trendlines: ITrendlineProperty[]) {}

    public getConfig(baseRenderConfig: IChartRendererConfig): IChartRendererConfig  {
        this.normalizeTrendlineData();

        return this.createTrendlineRenderConf(baseRenderConfig);
    }

    protected normalizeTrendlineData() {
        this.data = [];
        this.trendlines.map((trendline, trendlineIndex) => {
            trendline.initTrendlineDataSource(new DataSet());

            trendline.data.forEach((dataItem) => {
                const { category, categoryIndex } = dataItem;
                const categoryVal = typeof category === 'number' ? category : `${category}${CATEGORY_MARK}${categoryIndex}`;
                this.data.push({
                    ...dataItem,
                    ...getShapeAttr(dataItem.props, { isLine: true }),
                    // category可能同名，但是一般不会是日期坐标轴或者数字。因为现在的number类型会存在需要format或者Number，不能全部加隐藏索引
                    // 所以目前先再非number类型上加隐藏索引，用于支持同名的category进行分组
                    category: categoryVal,
                    specialGeometryType: SpecialGeometryType.trendLine,
                    trendlineIndex: trendline.idx,
                } as unknown as ChartUIDataItem);
            });
        });
    }

    protected createTrendlineRenderConf = (baseRenderConfig: IChartRendererConfig): IChartRendererConfig => {
        const { xScale, yScale, xAxis, yAxis, coordinate, seriesColor } = baseRenderConfig;
        const trendlineCoordinate = { ...coordinate };

        // 条形图的趋势线也需要坐标轴置换
        if (baseRenderConfig.type === CHART_TYPE.BAR_CHART) {
            trendlineCoordinate.actions = [...(trendlineCoordinate.actions ?? []), ['transpose']];
        }

        return {
            type: CHART_TYPE.LINE_CHART,
            data: this.data,
            geometryStyle: this.createFieldMappingCfg<ShapeStyle>((id): ShapeStyle => {
                const ChartUIDataItem = this.data.find(data => data.id === id);
                if (!ChartUIDataItem) return {};
                const { border } = ChartUIDataItem || {};

                const { outline, ...rest } = filterOriginStyle(ChartUIDataItem);
                const newOutline = { ...outline };
                const originStyle = { outline: newOutline, ...rest };

                return {
                    lineWidth: border?.lineWidth,
                    lineDash: border?.lineDash,
                    stroke: border?.stroke,
                    lineJoin: border?.lineJoin,
                    lineCap: border?.lineCap,
                    customStyles: generateCustomStyles(originStyle),
                };
            }, 'id'),
            // @ts-ignore
            geometryShape: 'smooth',
            seriesColor,
            xScale,
            yScale,
            xAxis: {
                // 趋势线在数据为空的时候，需要过滤该数据，会导致坐标轴与主图表的坐标轴不一致，这里把所有趋势线坐标轴label都隐藏，只关注趋势
                ...(xAxis as any),
                label: {
                    formatter: () => ' ',
                },
                title: null,
            },
            yAxis,
            coordinate: trendlineCoordinate,
            lineChartConfig: {
                pointVisible: false,
            },
        };
    };

    private createFieldMappingCfg = <T>(callback: (args: string) => T, field = 'id') => ({
        fields: [field],
        callback,
    });
}
